<script setup>
import BaseModal from "@/components/global-themed/BaseModal.vue";
import ModalContentWrapper from "@/components/global-themed/ui/modals/ModalContentWrapper.vue";
import ModalHeading from "@/components/global-themed/ui/modals/ModalHeading.vue";
import ModalHeader from "@/components/global-themed/ui/modals/ModalHeader.vue";
import ModalSubText from "@/components/global-themed/ui/modals/ModalSubText.vue";
import DefaultButton from "@/components/global-themed/buttons/DefaultButton.vue";
import {useMutation} from "@tanstack/vue-query";
import axios from "axios";
import {errorToast, successToast} from "@/composables/useToasts";
import {computed, onMounted, reactive, ref} from "vue";
import DefaultSelect from "@/components/global-themed/inputs/DefaultSelect.vue";
import DefaultInput from "@/components/global-themed/inputs/DefaultInput.vue";

const modalRequired = ref(false);
const hiddenByCookie = ref(false);

const form = reactive({
  preferred_contact_method: 'Email',
  phone: '',
  email: '',
});
const errors = ref({});

onMounted(() => {
  hiddenByCookie.value = document.cookie.includes('contact_info_modal_hidden=true');

  window.axios.get('/ajax/v2/contact-method').then(({data}) => {
    modalRequired.value = data.required;
    form.preferred_contact_method = data.preferred_contact_method ?? 'Email';
    form.phone = data.phone ?? '';
    form.email = data.email ?? '';
  })
})

const showModal = computed(() => {
  return modalRequired.value && !hiddenByCookie.value;
});

const {mutate, isPending, isSuccess} = useMutation({
  mutationFn: (data) => axios.post('/ajax/v2/contact-method', data),
  onError: (error) => {
    if (error.response?.status === 422) {
      errors.value = error.response.data.errors;
      errorToast('Check for errors and try again');
      return
    }

    errorToast('Something went wrong, please try again');
    console.error(error);
  },
  onSuccess: () => {
    successToast('Contact preferences saved, thanks')
    modalRequired.value = false;
  }
});

const handleDismiss = () => {
  document.cookie = 'contact_info_modal_hidden=true; path=/; max-age=86400';
  hiddenByCookie.value = true;
}

const handleSubmit = () => {
  errors.value = {};
  mutate(form);
}
</script>

<template>
  <Teleport
    v-if="showModal"
    to=".tailwind"
  >
    <BaseModal
      @close="handleDismiss"
    >
      <ModalContentWrapper class="!tw-pt-0">
        <ModalHeader>
          <ModalHeading>Staying in Touch</ModalHeading>
          <ModalSubText>
            <h3 class="tw-font-bold">
              How should we contact you if we need to reach out?
            </h3>
            <p class="tw-text-sm">
              Your contact preferences are used only for customer support and essential account communication.
            </p>
          </ModalSubText>
        </ModalHeader>
        <form
          class="tw-flex tw-flex-col tw-gap-6"
          @submit.prevent="handleSubmit"
        >
          <DefaultSelect
            id="preferred_contact_method"
            v-model="form.preferred_contact_method"
            label="Preferred Method of Contact"
            subtext="Our Customer Success team will use your preferred contact method whenever possible."
            :container-attrs="{
              class: '!tw-border-neutral !tw-border',
            }"
            :validation-errors="errors?.preferred_contact_method"
          >
            <option value="Email">
              Email
            </option>
            <option value="Phone">
              Phone
            </option>
            <option value="Video Call">
              Video Call (Zoom)
            </option>
            <option value="No Preference">
              No Preference
            </option>
          </DefaultSelect>

          <DefaultInput
            id="email"
            v-model="form.email"
            type="email"
            label="Email address"
            :validation-errors="errors?.email"
          />

          <DefaultInput
            id="phone"
            v-model="form.phone"
            type="text"
            label="Phone number"
            :validation-errors="errors?.phone"
          />

          <div class="tw-flex tw-items-center tw-gap-4 tw-self-end tw-w-full">
            <DefaultButton
              v-if="!isSuccess"
              :loading="isPending"
              :disabled="isPending || isSuccess"
              class="tw-ml-auto"
              @click="handleSubmit"
            >
              Confirm
            </DefaultButton>
          </div>
        </form>
      </ModalContentWrapper>
    </BaseModal>
  </Teleport>
</template>
